import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  MessageCircle,
  Send,
  Phone,
  Check,
  CheckCheck,
  Clock,
  ArrowLeft,
  Car,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Bot,
  User
} from 'lucide-react';

export const ChatWindow = ({
  selectedConversacion,
  mensajes,
  llmEnabled,
  llmLoading,
  nuevoMensaje,
  setNuevoMensaje,
  handleSendMessage,
  toggleLlmEnabled,
  loadingMore,
  scrollContainerRef,
  messagesEndRef,
  handleScroll,
  isMobileView,
  setShowChatOnMobile
}) => {
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusIcon = (estado, errorCode) => {
    switch (estado) {
      case 'enviando':
        return <Loader2 className="h-3 w-3 animate-spin" />;
      case 'enviado':
        return <Check className="h-3 w-3" />;
      case 'entregado':
        return <CheckCheck className="h-3 w-3" />;
      case 'leido':
        return <CheckCheck className="h-3 w-3 text-blue-400" />;
      case 'fallido':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 text-red-500" />
                  {errorCode === 131049 && (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 drop-shadow-sm animate-pulse" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {errorCode === 131049
                  ? "Bloqueado por Meta (Ecosistema - SPAM). Número penalizado."
                  : "Error al enviar el mensaje"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'pendiente':
        return <Clock className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'HH:mm', { locale: es });
  };

  const formatMessageDateSeparator = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
    }
  };

  if (!selectedConversacion) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground h-full">
        <div className="text-center">
          <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">Selecciona una conversación</p>
          <p className="text-sm">o inicia una nueva</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="p-4 border-b bg-card flex items-center gap-3">
        {isMobileView && (
          <Button variant="ghost" size="sm" onClick={() => setShowChatOnMobile(false)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary">
            {getInitials(selectedConversacion.nombreContacto)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{selectedConversacion.nombreContacto}</h2>
            {selectedConversacion.whatsappBloqueado && (
              <Badge variant="destructive" className="h-[20px] text-[10.5px] px-1.5 gap-1">
                <AlertTriangle className="h-3 w-3" />
                Penalizado por Meta
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
            <Phone className="h-3 w-3" />
            {selectedConversacion.telefono}
          </p>
        </div>
        {selectedConversacion.vehiculoId && (
          <Badge variant="outline" className="gap-1">
            <Car className="h-3 w-3" />
            Vehículo
          </Badge>
        )}

        {/* LLM Toggle */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 ml-2">
                <Switch
                  id="llm-toggle"
                  checked={llmEnabled}
                  onCheckedChange={toggleLlmEnabled}
                  disabled={llmLoading}
                  data-testid="llm-toggle"
                />
                {llmEnabled ? (
                  <Bot className="h-4 w-4 text-green-500" />
                ) : (
                  <User className="h-4 w-4 text-blue-500" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{llmEnabled ? 'Respuesta automática (IA)' : 'Respuesta manual'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Messages area */}
      <div 
        className="flex-1 p-4 overflow-y-auto flex flex-col" 
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {loadingMore && (
          <div className="flex justify-center p-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        <div className="space-y-3">
          {mensajes.map((mensaje, index) => {
            const currentDate = new Date(mensaje.createdAt).toDateString();
            const previousDate = index > 0 ? new Date(mensajes[index - 1].createdAt).toDateString() : null;
            const showDateSeparator = currentDate !== previousDate;
            
            return (
              <div key={mensaje.id} className="space-y-3">
                {showDateSeparator && (
                  <div className="flex justify-center my-4 opacity-80">
                    <Badge variant="secondary" className="text-[11px] font-medium shadow-sm px-3 py-0.5 whitespace-nowrap bg-muted">
                      {formatMessageDateSeparator(mensaje.createdAt)}
                    </Badge>
                  </div>
                )}
                <div
                  className={`flex ${mensaje.direccion === 'saliente' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2 ${mensaje.direccion === 'saliente'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted rounded-bl-md'
                      }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{mensaje.mensaje}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 ${mensaje.direccion === 'saliente' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                      <span className="text-xs">{formatMessageTime(mensaje.createdAt)}</span>
                      {mensaje.direccion === 'saliente' && getStatusIcon(mensaje.estado, mensaje.error_code)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message input */}
      <div className="p-4 border-t bg-card">
        {llmEnabled ? (
          <div className="flex items-center justify-center gap-2 py-2 text-muted-foreground">
            <Bot className="h-5 w-5 text-green-500" />
            <span className="text-sm">Respuesta automática activada - El asistente responde por ti</span>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Escribe un mensaje..."
              value={nuevoMensaje}
              onChange={(e) => setNuevoMensaje(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1"
              data-testid="message-input"
            />
            <Button onClick={handleSendMessage} disabled={!nuevoMensaje.trim()} data-testid="send-message-button">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
